import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ErrorPanel, RefreshingDot, SimulatedNotice } from '../../../components/PageState';
import { Badge, Button, FilterChips, Footnote, HeroBanner, Icon, Input, Panel, SectionCard, Select } from '../../../components/ui';
import { KpiCard, KpiCardSkeleton, StatChip } from '../../../components/KpiCard';
import { AGORA_SIMULADO, useApiResource } from '../../../lib/api';
import { useCompany } from '../../../lib/companyContext';
import { formatBRLFromCents, formatCount, formatPercentFromBps, maskPhoneDisplay, shortenName } from '../../../lib/format';
import { usePeriod } from '../../../lib/periodContext';
import { describeSelection } from '../../../lib/period';
import { fetchComercial } from '../api';
import { ItemDaFila } from '../components/ItemDaFila';
import { ModalVendaAssistida, type VendaAssistidaRegistrada } from '../components/ModalVendaAssistida';
import { OQueFazerAgoraComercial } from '../components/OQueFazerAgoraComercial';
import { PainelDeVendedoras } from '../components/PainelDeVendedoras';
import {
  avisosDoComercial,
  contagemPorEstado,
  contagemPorVendedora,
  estaAberto,
  FILTROS_INICIAIS,
  filtrarFila,
  filtrosAtivos,
  minutosDesde,
  ORDENS,
  ordenarFila,
  resumirFila,
  SLA_MINUTOS,
  slaPorVendedora,
  VENDEDORA_NENHUMA,
  VENDEDORA_TODAS,
  type AcaoDaFila,
  type FiltroEstado,
  type FiltrosDaFila,
  type OrdemDaFila,
} from '../fila';
import type { RefundPolicy } from '../../../types';
import type { ComercialOverview, EstadoDaComissao, HandoffItem } from '../types';
import { prazoCurto } from '../vendaAssistida';

/**
 * Comercial -- os leads que responderam a regua e cairam com as vendedoras.
 *
 * Pedido do dono (25/09/2026): o numero conectado costuma ser o do comercial;
 * muita gente tira duvida com a vendedora e fecha por ali. A comissao continua
 * do Converte Facil (ADR-001: o ultimo toque do motor em ate 7 dias). Esta
 * tela deixa isso VISIVEL (cada linha diz qual toque provocou a resposta e ate
 * quando a venda ainda credita) e COBRAVEL (registrar a venda assistida quando
 * nao ha webhook, pelo POST /api/leads/:leadId/venda-assistida).
 *
 * Decisoes nao obvias:
 *  - O que a pessoa muda na tela (atribuir, perder, registrar) vira um
 *    "remendo" por atendimento aplicado POR CIMA do dado do servidor. A
 *    atualizacao automatica (60 s) nao apaga o que foi feito; o remendo some
 *    sozinho quando o servidor passa a devolver o mesmo DESFECHO (estado +
 *    vendedora, ou estado + pedido na venda). Comparar todos os campos nunca
 *    casaria (hora do pagamento e 1a resposta sao calculadas aqui e no
 *    servidor de jeitos diferentes) e o remendo mascararia para sempre o que
 *    o servidor disser depois -- inclusive uma comissao revertida.
 *  - Filtros, ordem e "ver mais" moram no conteudo, que e remontado quando a
 *    empresa ou a janela mudam (chave): trocar de empresa volta ao inicio,
 *    a atualizacao automatica nao.
 *  - "Agora" e `AGORA_SIMULADO` na previa (o dado ficticio e relativo a ele) e
 *    o relogio real em producao: e o que faz "esperando há 12 min" bater.
 *  - Registrar venda chama o backend (mesma funcao do detalhe do lead); so o
 *    desfecho devolvido pelo servidor muda a linha para "Fechou". `duplicado`
 *    nao mexe na linha: nada novo foi gravado.
 */

const ESTADOS: readonly { readonly value: FiltroEstado; readonly label: string }[] = [
  { value: 'todos', label: 'Todos' },
  { value: 'aguardando', label: 'Aguardando' },
  { value: 'em_atendimento', label: 'Em atendimento' },
  { value: 'fechou', label: 'Fechou' },
  { value: 'perdeu', label: 'Perdeu' },
];

/** Acima disto os chips de vendedora viram um seletor (empresa grande, equipe grande). */
const MAX_CHIPS_DE_VENDEDORA = 5;
const PASSO_DA_LISTA = 12;

/** Campos que dizem se o servidor ja alcancou um remendo (ver cabecalho). */
function chavesDeConferencia(remendo: Partial<HandoffItem>): readonly (keyof HandoffItem)[] {
  const chaves: readonly (keyof HandoffItem)[] = remendo.status === 'fechou' ? ['status', 'orderRef'] : ['status', 'sellerId'];
  return chaves.filter((k) => k in remendo);
}

const COMISSAO_DO_DESFECHO: Readonly<Record<'creditado' | 'creditado_revertido' | 'sem_contato', EstadoDaComissao>> = {
  creditado: 'creditada',
  creditado_revertido: 'revertida',
  sem_contato: 'sem_comissao',
};

function delta(atual: number, anterior: number): string {
  if (anterior === 0) {
    return atual === 0 ? '▶ igual ao período anterior' : 'sem base no período anterior';
  }
  const pct = Math.round(((atual - anterior) / anterior) * 100);
  return pct === 0 ? '▶ igual ao período anterior' : `${pct > 0 ? '▲' : '▼'} ${Math.abs(pct)}% vs período anterior`;
}

function rolarAteFila(): void {
  document.getElementById('fila-do-comercial')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

export function Comercial(): JSX.Element {
  const { company } = useCompany();
  const { range, selection } = usePeriod();
  const buscar = useCallback((signal: AbortSignal) => fetchComercial(company.id, range, signal), [company.id, range]);
  const { state, reload } = useApiResource(buscar, 60_000);

  useEffect(() => {
    document.title = `Comercial — ${company.name} · Converte Fácil`;
  }, [company.name]);

  return (
    <section aria-labelledby="titulo-da-pagina" className="flex flex-col gap-6">
      <HeroBanner
        icon="usuarios"
        title="Comercial"
        badges={<Badge tone="brand" dot>Régua → vendedoras</Badge>}
        description={
          <>
            {company.name} · {describeSelection(selection)}. Quem respondeu a um toque da régua ("Tenho uma dúvida" ou digitando) e caiu com as vendedoras.
            A régua para na hora; a comissão do Converte Fácil continua valendo porque foi o toque que trouxe o lead de volta.
          </>
        }
        aside={<RefreshingDot active={state.status === 'success' && state.refreshing} />}
      />

      <Panel tone="brand" className="flex flex-wrap items-start gap-3 px-5 py-4">
        <span aria-hidden="true" className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-brand/15 text-brand">
          <Icon name="escudo" size="md" />
        </span>
        <div className="min-w-0 flex-1 text-sm text-ink">
          <p className="font-semibold">Regra da comissão nas vendas do comercial</p>
          <p className="text-ink-muted">
            Pagamento em até <strong className="text-ink">7 dias</strong> depois de um toque da régua é creditado ao <strong className="text-ink">último toque do motor</strong>, mesmo que a vendedora tenha fechado — ela fica como “fechado por”.
            Quando o pagamento não chega por webhook (Pix manual, link de outra plataforma), registre a venda aqui e ela entra pela mesma regra.
          </p>
        </div>
      </Panel>

      {state.status === 'loading' ? (
        <>
          <p role="status" className="sr-only">
            Carregando a fila do comercial
          </p>
          <div className="flex flex-col gap-4" aria-hidden="true">
            <KpiCardSkeleton emphasis />
            <div className="grid gap-4 sm:grid-cols-3">
              <KpiCardSkeleton />
              <KpiCardSkeleton />
              <KpiCardSkeleton />
            </div>
          </div>
        </>
      ) : null}
      {state.status === 'error' ? <ErrorPanel title="Não foi possível carregar o comercial" error={state.error} onRetry={reload} /> : null}
      {state.status === 'success' ? (
        <>
          {state.simulated ? (
            <SimulatedNotice>Fila, vendedoras e valores são fictícios e fixos. Atribuir e marcar perdido ficam só nesta tela; registrar venda passa pela mesma função do servidor, simulada.</SimulatedNotice>
          ) : null}
          <Conteudo
            key={`${company.id}|${range.from ?? ''}|${range.to ?? ''}`}
            overview={state.data}
            simulated={state.simulated}
            companyId={company.id}
            feeBps={company.feeBps}
            politicaDeEstorno={company.refundPolicy}
            onRecarregar={reload}
          />
        </>
      ) : null}
    </section>
  );
}

type Remendos = Readonly<Record<string, Partial<HandoffItem>>>;

function Conteudo({
  overview,
  simulated,
  companyId,
  feeBps,
  politicaDeEstorno,
  onRecarregar,
}: {
  readonly overview: ComercialOverview;
  readonly simulated: boolean;
  readonly companyId: string;
  readonly feeBps: number;
  readonly politicaDeEstorno: RefundPolicy | undefined;
  readonly onRecarregar: () => void;
}): JSX.Element {
  // `overview` entra na dependencia de proposito: em producao o "agora" anda a cada atualizacao.
  const agora = useMemo(() => (simulated ? AGORA_SIMULADO : new Date()), [simulated, overview]);
  const agoraMs = agora.getTime();
  const [remendos, setRemendos] = useState<Remendos>({});
  const [filtros, setFiltros] = useState<FiltrosDaFila>(FILTROS_INICIAIS);
  const [ordem, setOrdem] = useState<OrdemDaFila>('prioridade');
  const [limite, setLimite] = useState(PASSO_DA_LISTA);
  const [registrandoId, setRegistrandoId] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);

  // Remendo que o servidor ja alcancou (mesmos valores) deixa de existir.
  useEffect(() => {
    setRemendos((atual) => {
      let mudou = false;
      const proximo: Record<string, Partial<HandoffItem>> = {};
      for (const [id, remendo] of Object.entries(atual)) {
        const doServidor = overview.items.find((i) => i.id === id);
        const chaves = chavesDeConferencia(remendo);
        const alcancou = doServidor !== undefined && chaves.length > 0 && chaves.every((k) => doServidor[k] === remendo[k]);
        if (alcancou) {
          mudou = true;
        } else {
          proximo[id] = remendo;
        }
      }
      return mudou ? proximo : atual;
    });
  }, [overview.items]);

  useEffect(() => {
    if (aviso === null) {
      return undefined;
    }
    const t = window.setTimeout(() => setAviso(null), 3_200);
    return () => window.clearTimeout(t);
  }, [aviso]);

  const itens = useMemo<readonly HandoffItem[]>(
    () =>
      overview.items.map((i) => {
        const r = remendos[i.id];
        return r === undefined ? i : { ...i, ...r };
      }),
    [overview.items, remendos],
  );

  const vendedorasAtivas = useMemo(() => overview.sellers.filter((s) => s.active), [overview.sellers]);
  const resumo = useMemo(() => resumirFila(itens, agoraMs), [itens, agoraMs]);
  const ranking = useMemo(() => slaPorVendedora(itens, overview.sellers), [itens, overview.sellers]);
  const avisos = useMemo(() => avisosDoComercial(itens, overview.sellers, agoraMs, feeBps), [itens, overview.sellers, agoraMs, feeBps]);
  const porEstado = useMemo(() => contagemPorEstado(itens, filtros), [itens, filtros]);
  const porVendedora = useMemo(() => contagemPorVendedora(itens, filtros, overview.sellers), [itens, filtros, overview.sellers]);
  const filtrados = useMemo(() => ordenarFila(filtrarFila(itens, filtros), ordem, agoraMs), [itens, filtros, ordem, agoraMs]);
  const visiveis = filtrados.slice(0, limite);
  const abertosPorVendedora = useMemo(() => {
    const mapa = new Map<string, number>();
    for (const i of itens) {
      if (i.sellerId !== null && estaAberto(i)) {
        mapa.set(i.sellerId, (mapa.get(i.sellerId) ?? 0) + 1);
      }
    }
    return mapa;
  }, [itens]);
  const vendedorasDoSeletor = useMemo(() => vendedorasAtivas.map((s) => ({ id: s.id, name: s.name, abertos: abertosPorVendedora.get(s.id) ?? 0 })), [vendedorasAtivas, abertosPorVendedora]);
  const nomeDaVendedora = useCallback((id: string | null): string | null => overview.sellers.find((s) => s.id === id)?.name ?? null, [overview.sellers]);
  const registrando = registrandoId === null ? null : itens.find((i) => i.id === registrandoId) ?? null;
  const ativos = filtrosAtivos(filtros);
  const ordemAtual = ORDENS.find((o) => o.value === ordem);

  const mudarFiltros = (patch: Partial<FiltrosDaFila>): void => {
    setFiltros((atual) => ({ ...atual, ...patch }));
    setLimite(PASSO_DA_LISTA);
  };

  const limparTudo = (): void => {
    setFiltros(FILTROS_INICIAIS);
    setOrdem('prioridade');
    setLimite(PASSO_DA_LISTA);
  };

  const remendar = useCallback((id: string, patch: Partial<HandoffItem>, mensagem: string): void => {
    setRemendos((atual) => ({ ...atual, [id]: { ...(atual[id] ?? {}), ...patch } }));
    setAviso(mensagem);
  }, []);

  const aplicarAcao = (acao: AcaoDaFila): void => {
    setFiltros((atual) => ({
      ...atual,
      ...(acao.estado === undefined ? {} : { estado: acao.estado }),
      ...(acao.vendedora === undefined ? {} : { vendedora: acao.vendedora }),
    }));
    if (acao.ordem !== undefined) {
      setOrdem(acao.ordem);
    }
    setLimite(PASSO_DA_LISTA);
    rolarAteFila();
  };

  const filtrarVendedora = (vendedora: string): void => {
    mudarFiltros({ vendedora });
    rolarAteFila();
  };

  const fecharModal = useCallback((): void => setRegistrandoId(null), []);

  const aoRegistrar = useCallback(
    (registro: VendaAssistidaRegistrada): void => {
      const conversao = registro.resposta.conversion;
      if (registrandoId === null || conversao === 'duplicado') {
        return;
      }
      const alvo = itens.find((i) => i.id === registrandoId);
      remendar(
        registrandoId,
        {
          status: 'fechou',
          closedAt: registro.paidAt,
          closedAmountCents: registro.amountCents,
          orderRef: registro.orderRef,
          closedVia: 'manual',
          sellerId: registro.sellerId ?? alvo?.sellerId ?? null,
          firstResponseMinutes: alvo?.firstResponseMinutes ?? (alvo === undefined ? null : minutosDesde(alvo.repliedAt, agoraMs)),
          commissionState: COMISSAO_DO_DESFECHO[conversao],
          commissionCents: registro.resposta.commissionCents,
        },
        `Venda registrada · ${registro.desfecho.selo}`,
      );
      if (!simulated) {
        onRecarregar();
      }
    },
    [registrandoId, itens, agoraMs, simulated, onRecarregar, remendar],
  );

  const opcoesDeVendedora = [
    { value: VENDEDORA_TODAS, label: 'Todas', count: formatCount(porVendedora.get(VENDEDORA_TODAS) ?? 0) },
    { value: VENDEDORA_NENHUMA, label: 'Sem vendedora', count: formatCount(porVendedora.get(VENDEDORA_NENHUMA) ?? 0) },
    ...overview.sellers.map((s) => ({ value: s.id, label: s.name.split(/\s+/)[0] ?? s.name, count: formatCount(porVendedora.get(s.id) ?? 0) })),
  ];

  return (
    <div className="flex flex-col gap-6">
      <KpiCard
        emphasis
        label="Fechado pelo comercial após toque da régua"
        value={formatBRLFromCents(resumo.valorFechadoCents)}
        support={`${formatCount(resumo.fechadas)} ${resumo.fechadas === 1 ? 'venda' : 'vendas'} · comissão do Converte Fácil ${formatBRLFromCents(resumo.comissaoCents)}${
          resumo.semComissao > 0 ? ` · ${formatCount(resumo.semComissao)} sem comissão (fora dos 7 dias)` : ''
        } · ${delta(resumo.valorFechadoCents, overview.previous.valorFechadoCents)}`}
        aside={
          <span title="Taxa que vale para as próximas vendas. Vendas já apuradas mantêm a taxa da época.">
            <Badge tone="neutral">taxa atual {formatPercentFromBps(feeBps)}</Badge>
          </span>
        }
        icon={<Icon name="moeda" size="sm" />}
      />

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        <Dica texto="Leads que responderam e ainda não têm vendedora. Cada minuto aqui esfria a venda.">
          <StatChip label="Aguardando vendedora" value={formatCount(resumo.aguardando)} />
        </Dica>
        <Dica texto="Conversas abertas com uma vendedora. A régua está parada para elas.">
          <StatChip label="Em atendimento" value={formatCount(resumo.emAtendimento)} tone="whatsapp" />
        </Dica>
        <Dica texto="Fechadas sobre decididas (fechadas + perdidas) no período.">
          <StatChip label="Taxa de fechamento" value={formatPercentFromBps(resumo.taxaFechamentoBps)} />
        </Dica>
        <Dica
          texto={`Tempo médio entre a mensagem do lead e a primeira resposta da vendedora. Período anterior: ${
            overview.previous.tempoMedioRespostaMinutos === null ? '—' : prazoCurto(overview.previous.tempoMedioRespostaMinutos)
          }.`}
        >
          <StatChip label="1ª resposta média" value={resumo.mediaRespostaMin === null ? '—' : prazoCurto(resumo.mediaRespostaMin)} />
        </Dica>
        <Dica texto={`Respostas em até ${SLA_MINUTOS} min sobre o total respondido. Acima de 80% é o esperado.`}>
          <StatChip label={`No SLA de ${SLA_MINUTOS} min`} value={resumo.slaBps === null ? '—' : formatPercentFromBps(resumo.slaBps)} />
        </Dica>
        <Dica texto="Vendas sem webhook (Pix manual, outro link) registradas pela equipe. Entram na mesma regra de comissão.">
          <StatChip label="Vendas assistidas" value={formatCount(resumo.manuais)} />
        </Dica>
      </div>

      <OQueFazerAgoraComercial avisos={avisos} onAcao={aplicarAcao} />

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_20rem]">
        <SectionCard
          id="fila-do-comercial"
          title="Fila do comercial"
          icon="resposta"
          description="Cada linha diz qual toque provocou a resposta e até quando a venda ainda credita comissão: é o que prova que a régua trouxe o lead de volta."
          aside={
            aviso === null ? (
              <Badge tone="neutral">
                {formatCount(filtrados.length)} de {formatCount(itens.length)}
              </Badge>
            ) : (
              <span role="status">
                <Badge tone="good" dot>
                  {aviso}
                </Badge>
              </span>
            )
          }
          padded={false}
        >
          <div className="flex flex-col gap-3 px-5 pb-4">
            <FilterChips
              label="Filtrar por estado do atendimento"
              prefix="Estado:"
              value={filtros.estado}
              onChange={(estado) => mudarFiltros({ estado })}
              options={ESTADOS.map((e) => ({ value: e.value, label: e.label, count: formatCount(porEstado[e.value]) }))}
            />
            {overview.sellers.length <= MAX_CHIPS_DE_VENDEDORA ? (
              <FilterChips label="Filtrar por vendedora" prefix="Vendedora:" value={filtros.vendedora} onChange={(vendedora) => mudarFiltros({ vendedora })} options={opcoesDeVendedora} tone="accent" />
            ) : null}
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              {overview.sellers.length > MAX_CHIPS_DE_VENDEDORA ? (
                <Select size="sm" className="sm:w-56" value={filtros.vendedora} onChange={(e) => mudarFiltros({ vendedora: e.target.value })} aria-label="Filtrar por vendedora">
                  {opcoesDeVendedora.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label} ({o.count})
                    </option>
                  ))}
                </Select>
              ) : null}
              <Select
                size="sm"
                className="sm:w-72"
                value={ordem}
                onChange={(e) => setOrdem(ORDENS.find((o) => o.value === e.target.value)?.value ?? 'prioridade')}
                aria-label="Ordenar a fila"
                title={ordemAtual?.dica}
              >
                {ORDENS.map((o) => (
                  <option key={o.value} value={o.value}>
                    Ordenar: {o.label}
                  </option>
                ))}
              </Select>
              <div className="w-full sm:ml-auto sm:w-64">
                <Input
                  size="sm"
                  type="search"
                  value={filtros.busca}
                  onChange={(e) => mudarFiltros({ busca: e.target.value })}
                  placeholder="Nome, oferta, pedido ou final do telefone"
                  aria-label="Buscar na fila do comercial"
                />
              </div>
            </div>
            {ativos > 0 || ordem !== 'prioridade' ? (
              <p className="flex flex-wrap items-center gap-2 text-xs text-ink-subtle">
                <span>
                  Mostrando <strong className="tabular-nums text-ink">{formatCount(filtrados.length)}</strong> de {formatCount(itens.length)} atendimentos
                  {ativos > 0 ? ` · ${ativos} ${ativos === 1 ? 'filtro' : 'filtros'}` : ''} · ordem: {ordemAtual?.label.toLowerCase() ?? 'prioridade'}
                </span>
                <Button size="sm" variant="ghost" icon="x" onClick={limparTudo}>
                  Limpar
                </Button>
              </p>
            ) : null}
          </div>

          {itens.length === 0 ? (
            <div className="border-t border-line px-5 py-8 text-center">
              <p className="text-sm font-medium text-ink">Ninguém respondeu à régua neste período</p>
              <p className="mx-auto mt-1 max-w-md text-sm text-ink-subtle">
                Quando um lead tocar em “Tenho uma dúvida” ou escrever depois de um toque, ele aparece aqui na hora, a régua para e a vendedora assume. Aumente a janela no cabeçalho para ver períodos anteriores.
              </p>
            </div>
          ) : visiveis.length === 0 ? (
            <div className="border-t border-line px-5 py-8 text-center">
              <p className="text-sm font-medium text-ink">Nenhum atendimento com esses filtros</p>
              <p className="mx-auto mt-1 max-w-md text-sm text-ink-subtle">O número de cada chip já conta com os outros filtros ativos: um chip com 0 não traz ninguém. Tire um filtro ou limpe tudo.</p>
              <div className="mt-3">
                <Button size="sm" variant="secondary" icon="x" onClick={limparTudo}>
                  Limpar filtros
                </Button>
              </div>
            </div>
          ) : (
            <ul className="divide-y divide-line/70 border-t border-line" aria-label="Atendimentos do comercial">
              {visiveis.map((item) => (
                <ItemDaFila
                  key={item.id}
                  item={item}
                  agoraMs={agoraMs}
                  vendedoras={vendedorasDoSeletor}
                  nomeDaVendedora={nomeDaVendedora(item.sellerId)}
                  onAtribuir={(sellerId) =>
                    remendar(
                      item.id,
                      {
                        sellerId,
                        status: item.status === 'aguardando' && sellerId !== null ? 'em_atendimento' : item.status,
                        firstResponseMinutes: item.firstResponseMinutes ?? (sellerId === null ? null : minutosDesde(item.repliedAt, agoraMs)),
                      },
                      sellerId === null ? 'Vendedora removida' : `Atribuído a ${nomeDaVendedora(sellerId) ?? 'vendedora'}`,
                    )
                  }
                  onRegistrar={() => setRegistrandoId(item.id)}
                  onPerdeu={() => remendar(item.id, { status: 'perdeu' }, `${shortenName(item.leadName)} marcado como perdido`)}
                />
              ))}
            </ul>
          )}
          {filtrados.length > visiveis.length ? (
            <div className="flex flex-wrap items-center gap-3 border-t border-line px-5 py-3">
              <Button variant="secondary" size="sm" icon="chevron" onClick={() => setLimite((l) => l + PASSO_DA_LISTA)}>
                Ver mais {formatCount(Math.min(PASSO_DA_LISTA, filtrados.length - visiveis.length))}
              </Button>
              <span className="text-xs text-ink-subtle">
                {formatCount(visiveis.length)} de {formatCount(filtrados.length)} na lista
              </span>
            </div>
          ) : null}
        </SectionCard>

        <div className="flex flex-col gap-4">
          <SectionCard id="numero-do-comercial" title="Número do comercial" icon="whatsapp" description="O número conectado ao Converte Fácil. As respostas da régua caem aqui.">
            <p className="text-lg font-semibold tabular-nums text-ink">{maskPhoneDisplay(overview.whatsappNumberE164)}</p>
            <p className="mt-1 text-xs text-ink-subtle">Conectado pela Evolution API · quem atende é a equipe comercial</p>
            <div className="mt-3">
              <Link to={{ pathname: '/configuracoes', search: window.location.search }} className="text-sm font-medium text-brand hover:underline">
                Gerenciar em Configurações → Integrações
              </Link>
            </div>
          </SectionCard>
          <PainelDeVendedoras linhas={ranking} slaDaEquipeBps={resumo.slaBps} mediaDaEquipeMin={resumo.mediaRespostaMin} vendedoraAtiva={filtros.vendedora} onFiltrar={filtrarVendedora} />
          <Footnote>
            Um lead que responde deixa a régua na hora: nenhum toque seguinte é enviado enquanto a vendedora atende. Se a venda não vier por webhook em 7 dias, registre-a para a comissão não se perder.
          </Footnote>
        </div>
      </div>

      <ModalVendaAssistida
        open={registrando !== null}
        onClose={fecharModal}
        leadId={registrando?.leadId ?? ''}
        companyId={companyId}
        titulo="Registrar venda pela vendedora"
        descricao={
          registrando === null ? undefined : (
            <>
              {shortenName(registrando.leadName)} · {registrando.offerName} · respondeu ao <strong className="text-ink">{registrando.touchLabel}</strong>. A venda entra no motor como pagamento manual e a
              comissão segue a regra dos 7 dias.
            </>
          )
        }
        valorSugeridoCents={registrando?.amountCents ?? 0}
        feeBps={feeBps}
        toque={registrando === null ? null : { at: registrando.touchAt, rotulo: registrando.touchLabel }}
        agora={agora}
        vendedoras={vendedorasAtivas}
        vendedoraInicialId={registrando?.sellerId ?? null}
        politicaDeEstorno={politicaDeEstorno}
        onRegistrada={aoRegistrar}
      />
    </div>
  );
}

/** Tooltip nativo em termos de negocio para os indicadores menores. */
function Dica({ texto, children }: { readonly texto: string; readonly children: JSX.Element }): JSX.Element {
  return (
    <div title={texto} className="min-w-0">
      {children}
    </div>
  );
}
